import React, { useEffect, useRef, useState } from "react"
import { useQuery, useMutation } from "@apollo/client"
import { Modal, Button } from "reactstrap"

import ConfirmationPopup from "../../../Material/ConfirmationPopup"
import DatetimeRenderer from "../../../Material/DatetimeRenderer"
import AIAnalysisPanel from "./AIAnalysisPanel"
import DocumentViewer from "./DocumentViewer"
import ModalSkeleton from "./ModalSkeleton"
import PanelResizer, { DEFAULT_PANEL_WIDTH } from "./PanelResizer"
import HistoryList from "../../../Material/HistoryList"
import { DocumentDetailsPanel, OwnerBadge } from "./DocumentInfoPanels"
import { IncidentDocumentQuery, UpdateIncidentDocumentMutation } from "./queries"
import "./IncidentDocumentModal.css"

const TABS = [
    { key: "details", label: "Details" },
    { key: "analysis", label: "AI Analysis" },
    { key: "history", label: "History" },
]

const AI_ENABLED_DOCUMENT_TYPES = ["Police Report"]

const FooterAction = ({ label, onClick }) => (
    <Button className="idm-btn-secondary" onClick={onClick}>{label}</Button>
)

/**
 * Fullscreen viewer for a single incident document.
 *
 * Handed nothing but a document's global id -- it fetches, renders and analyses on its
 * own. Deliberately separate from Material/DocumentModal/DocumentModalNew, which serves
 * driver and car documents and carries their field-validation and OCR flows; nothing
 * there applies to an incident document.
 *
 * Editing notes, retyping and archiving are handled in here: they need nothing but the
 * document itself, and the same mutation backs all three. Moving is deliberately absent
 * -- it has always been a bulk action on the document list, and putting it here would be
 * a second way to do it rather than parity with what the old viewer offered.
 */
const IncidentDocumentModal = ({
    documentId,
    isOpen,
    toggle,
    onChanged,
    onPrev,
    onNext,
    positionLabel,
}) => {
    const [activeTab, setActiveTab] = useState("details")
    const [actionError, setActionError] = useState(null)
    const [confirmingArchive, setConfirmingArchive] = useState(false)
    const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH)
    const panelRef = useRef(null)
    const { data, loading, error, refetch } = useQuery(IncidentDocumentQuery, {
        variables: { id: documentId },
        skip: !documentId || !isOpen,
        fetchPolicy: "cache-and-network",
    })

    const [updateDocument, { loading: saving }] = useMutation(UpdateIncidentDocumentMutation)

    const document = data && data.incidentDocument

    // Fed to CSS as a custom property rather than an inline style, so the panel's
    // appearance stays in the stylesheet and only the number comes from here. Depends on
    // `document` too: the panel does not exist until the document has loaded.
    useEffect(() => {
        if (panelRef.current) {
            panelRef.current.style.setProperty("--idm-panel-width", `${panelWidth}px`)
        }
    }, [panelWidth, document])
    const documentTypeName = document && document.documentType ? document.documentType.name : ""
    const analysisAvailable = AI_ENABLED_DOCUMENT_TYPES.includes(documentTypeName)
    const visibleTabs = TABS.filter((tab) => tab.key !== "analysis" || analysisAvailable)
    const currentTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : "details"

    const toggleArchived = async () => {
        setActionError(null)
        try {
            const { data: result } = await updateDocument({
                variables: { input: { incidentDocumentId: documentId, isArchived: !document.isArchived } },
            })
            const payload = result && result.updateIncidentDocument
            if (!payload || !payload.ok) {
                const message = payload && payload.errors && payload.errors[0] && payload.errors[0].messages[0]
                setActionError(message || "Couldn't update this document.")
                return
            }
            setConfirmingArchive(false)
            if (onChanged) onChanged()
            if (toggle) toggle()
        } catch (e) {
            setActionError(e.message)
        }
    }

    const closeArchiveConfirmation = () => {
        setConfirmingArchive(false)
        setActionError(null)
    }

    return (
        <Modal className="idm-modal" isOpen={isOpen} toggle={toggle} fullscreen>
            <div className="idm-shell">
                {loading && !document && <ModalSkeleton />}
                {error && <div className="idm-load-error">Couldn&rsquo;t load this document: {error.message}</div>}
                {!loading && !error && !document && <div className="idm-load-error">This document no longer exists.</div>}

                {!!document && (
                    <>
                        <div className="idm-header">
                            <div className="idm-header-identity">
                                <span className="idm-file-icon">
                                    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                                        <path d="M14 3v5h5" /><path d="M9 13h6" /><path d="M9 17h4" />
                                    </svg>
                                </span>
                                <div className="idm-header-text">
                                    <div className="idm-header-title-row">
                                        <span className="idm-header-title">{documentTypeName || "Document"}</span>
                                        <OwnerBadge slug={document.slug} />
                                        {document.isArchived && <span className="idm-badge idm-badge-archived">ARCHIVED</span>}
                                    </div>
                                    <span className="idm-header-meta">
                                        Uploaded {document.dateAdded ? <DatetimeRenderer datetime={document.dateAdded} /> : "--"}
                                        {" "}by {document.createdBy ? document.createdBy.username : "--"}
                                    </span>
                                </div>
                            </div>
                            <div className="idm-header-actions">
                                <a className="idm-icon-btn" href={document.url} target="_blank" rel="noreferrer" title="Download" aria-label="Download">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" />
                                    </svg>
                                </a>
                                <button type="button" className="idm-icon-btn" onClick={toggle} title="Close" aria-label="Close">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <ConfirmationPopup
                            open={confirmingArchive}
                            message={`Are you sure you want to ${document.isArchived ? "unarchive" : "archive"} this document?`}
                            loading={saving}
                            error={actionError || ""}
                            action="Yes"
                            color="danger"
                            handleClose={closeArchiveConfirmation}
                            confirmAction={toggleArchived}
                        />

                        <div className="idm-body">
                            <DocumentViewer url={document.url} fileName={document.document} />

                            <PanelResizer width={panelWidth} onWidthChange={setPanelWidth} />

                            <div className="idm-side-panel" ref={panelRef}>
                                <div className="idm-tabs">
                                    {visibleTabs.map((tab) => (
                                        <button
                                            type="button"
                                            key={tab.key}
                                            className={`idm-tab ${currentTab === tab.key ? "idm-tab-active" : ""}`}
                                            onClick={() => setActiveTab(tab.key)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                                {currentTab === "details" && (
                                    <DocumentDetailsPanel
                                        document={document}
                                        onSaved={() => {
                                            refetch()
                                            if (onChanged) onChanged()
                                        }}
                                    />
                                )}
                                {currentTab === "analysis" && <AIAnalysisPanel documentId={documentId} />}
                                {currentTab === "history" && (
                                    <div className="idm-panel-body idm-history-panel">
                                        <HistoryList
                                            objectIds={[document.id]}
                                            subject={documentTypeName || "this document"}
                                            emptyText="No history found for this document."
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="idm-footer">
                            <div className="idm-footer-nav">
                                {!!onPrev && <Button className="idm-btn-secondary" onClick={onPrev}>Prev</Button>}
                                {!!positionLabel && <span className="idm-footer-position">{positionLabel}</span>}
                                {!!onNext && <Button className="idm-btn-secondary" onClick={onNext}>Next</Button>}
                            </div>
                            <div className="idm-footer-actions">
                                <FooterAction
                                    label={document.isArchived ? "Unarchive" : "Archive"}
                                    onClick={() => setConfirmingArchive(true)}
                                />
                                <Button className="idm-btn-close" onClick={toggle}>Close</Button>
                            </div>
                        </div>

                    </>
                )}
            </div>
        </Modal>
    )
}

export default IncidentDocumentModal
